import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { apiFetch } from "../lib/api";

interface AuditRow {
  id: string;
  userEmail: string | null;
  userRoleSnapshot: string | null;
  action: string;
  entityType: string;
  entityId: string;
  beforeJson: string | null;
  afterJson: string | null;
  ipAddress: string | null;
  occurredAt: string;
}

function Audit() {
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [filter, setFilter] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const load = (q?: string) => {
    const url = q ? `/api/admin/audit?action=${encodeURIComponent(q)}` : "/api/admin/audit";
    apiFetch<{ rows: AuditRow[] }>(url).then((r) => setRows(r.rows)).catch((e: Error) =>
      setErr(e.message),
    );
  };
  useEffect(() => load(), []);

  return (
    <div className="space-y-3">
      <div className="panel">
        <div className="text-xs text-slate-400 uppercase tracking-wide mb-1">Admin</div>
        <h1 className="text-2xl font-semibold mb-3">Audit log</h1>
        <div className="flex gap-2 items-center">
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by action prefix (e.g. subscription.)"
            className="border border-slate-300 rounded px-2 py-1 text-sm flex-1"
          />
          <button
            onClick={() => load(filter)}
            className="rounded bg-slate-900 text-white text-sm px-3 py-1"
          >
            Apply
          </button>
        </div>
      </div>
      {err && <div className="panel text-red-600">{err}</div>}
      <div className="panel">
        {rows.length === 0 ? (
          <div className="text-slate-400 text-sm">No audit rows match.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 text-left">
              <tr>
                <th className="py-1">When</th>
                <th>Who</th>
                <th>Action</th>
                <th>Entity</th>
                <th>Diff</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 align-top">
                  <td className="py-1">
                    <div>{new Date(r.occurredAt).toLocaleString()}</div>
                    <div className="text-xs text-slate-400">{r.ipAddress}</div>
                  </td>
                  <td>
                    <div>{r.userEmail ?? "—"}</div>
                    <div className="text-xs text-slate-400">{r.userRoleSnapshot}</div>
                  </td>
                  <td>
                    <code className="text-xs">{r.action}</code>
                  </td>
                  <td>
                    <div>
                      <span className="rounded bg-slate-100 px-1.5 text-xs">
                        {r.entityType}
                      </span>
                    </div>
                    <Link
                      to={
                        r.entityType === "subscription" || r.entityType === "screening"
                          ? "/family/$familyId"
                          : "/family/$familyId"
                      }
                      params={{ familyId: r.entityId }}
                      className="text-xs text-slate-500 hover:underline"
                    >
                      {r.entityId.slice(-12)}
                    </Link>
                  </td>
                  <td className="text-xs">
                    {r.beforeJson && (
                      <div className="text-red-600">- {r.beforeJson.slice(0, 80)}</div>
                    )}
                    {r.afterJson && (
                      <div className="text-green-700">+ {r.afterJson.slice(0, 80)}</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export const Route = createFileRoute("/audit")({ component: Audit });
